import type { PortOut, SwitchOut } from "../../api/types/siteSwitch";

// Just the switch fields the instructions need — keeps the function easy to call
// and test (a full SwitchOut satisfies it).
type SwitchInstructionInput = Pick<
  SwitchOut,
  "identity" | "slug" | "model" | "management_vlan_tag" | "uplink_port" | "maintenance_port" | "notes"
>;

// Pure, deterministic: render a web-UI-managed switch's recorded intent as human
// setup steps. tag→name via the template (bare tag if unknown); ports sorted by
// number. No I/O — unit-tested; also used by WebUiInstructionsPanel.
export function buildSwitchInstructions(
  sw: SwitchInstructionInput,
  ports: PortOut[],
  nameByTag: Map<number, string>,
): string[] {
  const vlanLabel = (tag: number) => {
    const name = nameByTag.get(tag);
    return name ? `${tag} (${name})` : `${tag}`;
  };

  const lines: string[] = [];
  lines.push(`Switch: ${sw.identity || sw.slug}${sw.model ? ` (${sw.model})` : ""}`);
  if (sw.management_vlan_tag != null) lines.push(`Management VLAN: ${vlanLabel(sw.management_vlan_tag)}`);
  if (sw.uplink_port != null) lines.push(`Uplink port: ${sw.uplink_port}`);
  if (sw.maintenance_port != null) lines.push(`Maintenance port: ${sw.maintenance_port}`);

  for (const p of [...ports].sort((a, b) => a.number - b.number)) {
    const head = `Port ${p.number}${p.name ? ` (${p.name})` : ""}`;
    if (p.mode === "access") {
      lines.push(
        `${head}: access${p.native_vlan_tag != null ? ` — untagged VLAN ${vlanLabel(p.native_vlan_tag)}` : ""}`,
      );
    } else {
      const parts: string[] = [];
      if (p.native_vlan_tag != null) parts.push(`native VLAN ${vlanLabel(p.native_vlan_tag)}`);
      if (p.tagged_vlan_tags.length) parts.push(`tagged VLANs ${p.tagged_vlan_tags.map(vlanLabel).join(", ")}`);
      lines.push(`${head}: ${p.mode}${parts.length ? ` — ${parts.join("; ")}` : ""}`);
    }
  }

  for (const note of sw.notes) lines.push(`Note: ${note}`);
  return lines;
}
